/*
 * This file is part of glowingblue/gamilytics.
 *
 * Copyright (c) 2023 Glowing Blue AG.
 * Authors: Rafael Horvat.
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import Component from 'flarum/common/Component';
import ItemList from 'flarum/common/utils/ItemList';
import extractText from 'flarum/common/utils/extractText';
import listItems from 'flarum/common/helpers/listItems';
import Button from 'flarum/common/components/Button';
import Dropdown from 'flarum/common/components/Dropdown';
import app from 'flarum/forum/app';
import { slug } from '../../../common';
import LinkButton from 'flarum/common/components/LinkButton';
import Icon from 'flarum/common/components/Icon';
import SelectDropdown from 'flarum/common/components/SelectDropdown';
const t = function () {
  return app.translator.trans(...arguments);
};
const prfx = `${slug}.forum.leaderboard.toolbar`;
export default class LeaderboardToolbar extends Component {
  oninit(vnode) {
    super.oninit(vnode);
  }
  view() {
    return m("div", {
      className: "LeaderboardToolbar IndexPage-toolbar"
    }, m("ul", {
      className: "IndexPage-toolbar-view"
    }, listItems(this.viewItems().toArray())), m("ul", {
      className: "IndexPage-toolbar-action"
    }, listItems(this.actionItems().toArray())));
  }
  get currentParams() {
    let currentParams = m.route.param();
    currentParams = {
      ...currentParams
    };
    // `key` is Mithril's own route param and must not travel into generated hrefs.
    delete currentParams.key;
    return currentParams;
  }
  viewItems() {
    const items = new ItemList();
    const timeSpans = ['day', 'week', 'month', 'year', 'all'];
    const currentTimeSpan = this.currentParams.timeSpan || 'month';
    const hideTimeFilter = app.forum.attribute(`${slug}.leaderboard.hide_time_filter`) || false;
    if (!hideTimeFilter) {
      items.add('time', m(Dropdown, {
        buttonClassName: "Button",
        label: t(`${prfx}.time_spans.${currentTimeSpan}`)
      }, timeSpans.map(timeSpan => {
        const active = timeSpan === currentTimeSpan;
        const params = {
          ...this.currentParams,
          timeSpan: timeSpan
        };
        if (timeSpan === 'month') {
          // month is the default, so it stays out of the URL
          delete params.timeSpan;
        }
        return m(LinkButton, {
          icon: active ? 'fas fa-check' : true,
          active: active,
          href: app.route(app.current.data.routeName, params)
        }, t(`${prfx}.time_spans.${timeSpan}`));
      })), 100);
    }
    const hideGroupFilter = app.forum.attribute(`${slug}.leaderboard.hide_group_filter`) || false;
    if (!hideGroupFilter) {
      items.add('groupFilter', m(SelectDropdown, {
        defaultLabel: t(`${prfx}.group_filter_button`),
        caretIcon: 'fas fa-caret-down',
        buttonClassName: "Button"
      }, this.groupItems().toArray()), 80);
    }
    const hideLevelFilter = app.forum.attribute(`${slug}.leaderboard.hide_level_filter`) || !app.forum.attribute(`${slug}.levels.enabled`);
    if (!hideLevelFilter) {
      items.add('levelFilter', m(SelectDropdown, {
        defaultLabel: t(`${prfx}.level_filter_button`),
        caretIcon: 'fas fa-caret-down',
        buttonClassName: "Button"
      }, this.levelItems().toArray()), 70);
    }
    return items;
  }
  groupItems() {
    const items = new ItemList();
    const currentGroupId = this.currentParams.group || null;
    const allGroupsParams = {
      ...this.currentParams
    };
    delete allGroupsParams.group;
    items.add('all', m(LinkButton, {
      icon: !currentGroupId ? 'fas fa-check' : true,
      active: !currentGroupId,
      href: app.route(app.current.data.routeName, allGroupsParams)
    }, t(`${prfx}.all_groups`)));
    app.store.all('groups').filter(group => group.id() !== '2' && group.id() !== '3') // Hide members & guests
    .forEach(group => {
      const groupId = group.id();
      const groupName = group.namePlural();
      const active = groupId === currentGroupId;
      const params = {
        ...this.currentParams,
        group: groupId
      };
      items.add(groupName, m(LinkButton, {
        icon: active ? 'fas fa-check' : true,
        active: active,
        href: app.route(app.current.data.routeName, params)
      }, m("span", {
        style: {
          color: group.color()
        }
      }, m(Icon, {
        name: group.icon()
      }), " ", groupName)));
    });
    return items;
  }
  levelItems() {
    const items = new ItemList();
    const currentLevel = this.currentParams.level || null;
    const allLevelsParams = {
      ...this.currentParams
    };
    delete allLevelsParams.level;
    items.add('all', m(LinkButton, {
      icon: !currentLevel ? 'fas fa-check' : true,
      active: !currentLevel,
      href: app.route(app.current.data.routeName, allLevelsParams)
    }, t(`${prfx}.all_levels`)));
    const levels = Array.from(Array(10).keys()).map(i => (i + 1).toString());
    levels.forEach(level => {
      const active = level === currentLevel;
      const params = {
        ...this.currentParams,
        level: level
      };
      items.add(`level-${level}`, m(LinkButton, {
        icon: active ? 'fas fa-check' : true,
        active: active,
        href: app.route(app.current.data.routeName, params)
      }, extractText(t(`${prfx}.level`, {
        level
      }))));
    });
    return items;
  }
  actionItems() {
    const items = new ItemList();
    items.add('refresh', m(Button, {
      title: t('core.forum.index.refresh_tooltip'),
      icon: 'fas fa-arrows-rotate',
      className: 'Button Button--icon',
      onclick: () => {
        // The store has already alerted on failure.
        app.leaderboard.refresh().catch(() => {}).finally(() => m.redraw());
      }
    }));
    return items;
  }
}
flarum.reg.add('glowingblue-gamilytics', 'forum/leaderboards/components/LeaderboardToolbar', LeaderboardToolbar);